import { Component } from '@theme/component';

/** Softens promotional links during scrolling without hiding their destinations. */
class FloatingShopLinks extends Component {
  /** @type {AbortController | undefined} */
  #controller;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #idleTimer;
  #motion = window.matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    super.connectedCallback();
    this.#connect();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#cleanup();
    this.#connect();
  }

  #connect() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    // One state change per scrolling session; no frame loop or layout reads.
    // Desktop scrolls .page-wrapper; mobile scrolls the document. Capture both.
    window.addEventListener('scroll', this.#onScroll, { capture: true, passive: true, signal });
    window.addEventListener('scrollend', this.#onScrollEnd, { capture: true, signal });
    window.addEventListener('blur', this.#restore, { signal });
    this.#motion.addEventListener('change', this.#restore, { signal });
    this.addEventListener('focusin', this.#restore, { signal });
    this.addEventListener('pointerenter', this.#restore, { signal });
  }

  /** @param {Event} event */
  #isPageScroll(event) {
    const target = event.target;
    return target === window || target === document || target === document.scrollingElement ||
      (target instanceof Element && target.matches('.page-wrapper'));
  }

  /** @param {Event} event */
  #onScroll = (event) => {
    if (this.#motion.matches || !this.#isPageScroll(event)) return;
    this.classList.add('is-scrolling');
    clearTimeout(this.#idleTimer);
    // Fallback for browsers without scrollend and for interrupted touch scrolling.
    this.#idleTimer = setTimeout(this.#restore, 700);
  };

  /** @param {Event} event */
  #onScrollEnd = (event) => {
    if (this.#isPageScroll(event)) this.#restore();
  };

  #restore = () => {
    clearTimeout(this.#idleTimer);
    this.classList.remove('is-scrolling');
  };

  #cleanup() {
    this.#controller?.abort();
    this.#restore();
  }

  disconnectedCallback() {
    this.#cleanup();
    super.disconnectedCallback();
  }
}

if (!customElements.get('floating-shop-links')) customElements.define('floating-shop-links', FloatingShopLinks);
